import React, { useEffect, useRef, useState } from 'react';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useReducedMotion,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Check,
  Maximize2,
  X,
} from 'lucide-react';

import PageHero from '@/components/layout/PageHero';
import CtaBand from '@/components/sections/CtaBand';
import { viewportOnce } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { usePortfolioDetail } from '@/hooks/usePortfolioDetail';
import { usePortfolioData } from '@/hooks/usePortfolioData';

const EASE = [0.22, 1, 0.36, 1];

/* ─── Data helpers ──────────────────────────────────────────────────────── */

function prettyCategory(c) {
  if (!c) return '';
  const map = { web: 'Web', mobile: 'Mobile', ai: 'AI & ML', ecom: 'E-Commerce', biz: 'Enterprise', uiux: 'UI/UX', games: 'Games' };
  return map[c] || c;
}

// Highlights can be plain strings or {icon,title,value} objects → normalise to strings.
function normalizeHighlights(highlights = []) {
  return (Array.isArray(highlights) ? highlights : [])
    .map((h) => {
      if (typeof h === 'string') return h;
      if (h && h.title && h.value) return `${h.title} — ${h.value}`;
      return h?.value || h?.title || '';
    })
    .filter(Boolean);
}

// Merge the gold `tag` line (e.g. "React · Node.js · PostgreSQL") with the `tech`
// array, de-duplicated — so the stack section is never a single lonely chip.
function parseTechList(tag, tech) {
  const out = [];
  const seen = new Set();
  const push = (name, icon_url = null) => {
    const n = String(name || '').trim();
    if (!n) return;
    const key = n.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ name: n, icon_url });
  };
  (Array.isArray(tech) ? tech : []).forEach((t) =>
    typeof t === 'string' ? push(t) : push(t?.name, t?.icon_url)
  );
  if (typeof tag === 'string') tag.split(/[·•|,/]+/).forEach((s) => push(s));
  return out;
}

// Find the first highlight that carries a number → feature it as a signature stat.
function extractMetric(highlights = []) {
  for (const h of normalizeHighlights(highlights)) {
    const m = h.match(/(\d[\d.,]*)\s*(%|x|×|\+|k|m|bn)?/i);
    if (m && /\d/.test(m[1])) {
      return { value: (m[1] + (m[2] || '')).replace(/\s+/g, ''), text: h };
    }
  }
  return null;
}

/* ─── Shared chapter heading (the dossier's cohesion device) ────────────── */

function ChapterHeading({ eyebrow, title, light = false, align = 'left' }) {
  const center = align === 'center';
  return (
    <div className={center ? 'text-center max-w-3xl mx-auto' : 'max-w-3xl'}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={viewportOnce}
        transition={{ duration: 0.5, ease: EASE }}
        className={`flex items-center gap-3 ${center ? 'justify-center' : ''}`}
      >
        <span className={`text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase ${light ? 'text-white/75' : 'text-brandblue'}`}>
          {eyebrow}
        </span>
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={viewportOnce}
        transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
        className={`mt-4 text-3xl sm:text-4xl lg:text-[2.9rem] font-bold tracking-tight leading-[1.06] ${light ? 'text-white' : 'text-navy-deep'}`}
      >
        {title}
      </motion.h2>
    </div>
  );
}

/* Gold "visit live site" anchor — matches the PrimaryButton sheen language. */
function VisitLink({ href, children = 'Live View', className }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="pd-visit-live-btn"
      className={cn(
        'group relative inline-flex items-center gap-2 overflow-hidden rounded-full px-7 py-3.5',
        'text-[12px] tracking-[0.18em] uppercase font-bold text-white bg-gold-gradient shadow-gold',
        'transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(201,150,46,0.5)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E] focus-visible:ring-offset-2',
        className
      )}
    >
      <span className="relative z-10 inline-flex items-center gap-2">
        {children}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </span>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-full bg-gold-sheen opacity-0 group-hover:opacity-100 group-hover:animate-[sheen_900ms_ease-in-out]"
      />
    </a>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────────── */

export default function PortfolioDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, loading, notFound } = usePortfolioDetail(slug);
  const { data: listData } = usePortfolioData();
  const reducedMotion = useReducedMotion();

  // Update page meta from the portfolio item
  useEffect(() => {
    if (!data?.meta) return;
    if (data.meta.title) document.title = data.meta.title;
    const setMeta = (sel, attr, name, content) => {
      if (!content) return;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('meta[name="description"]', 'name', 'description', data.meta.description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', data.meta.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', data.meta.description);
    if (data.meta.og_image) {
      const abs = /^https?:\/\//i.test(data.meta.og_image)
        ? data.meta.og_image
        : `${window.location.origin}${data.meta.og_image.startsWith('/') ? '' : '/'}${data.meta.og_image}`;
      setMeta('meta[property="og:image"]', 'property', 'og:image', abs);
    }
  }, [data?.meta]);

  if (notFound) {
    return (
      <>
        <PageHero title="Project Not Found" subtitle="The project you're looking for doesn't exist." crumb="Portfolio" />
        <section className="py-16">
          <div className="container mx-auto text-center">
            <Link to="/portfolio" data-testid="pd-back-link" className="text-sm font-semibold text-brandblue hover:text-[#C9962E]">
              ← Back to all work
            </Link>
          </div>
        </section>
        <CtaBand />
      </>
    );
  }

  if (loading || !data) {
    return (
      <section className="py-32 bg-navy-deep">
        <div className="container mx-auto text-center text-sm text-white/70">Loading project…</div>
      </section>
    );
  }

  const p = data.portfolio;
  const prev = data.prev_slug;
  const next = data.next_slug;

  const gallery = (Array.isArray(p.gallery) ? p.gallery : []).map((g) => (typeof g === 'string' ? g : g?.url || ''));
  const techList = parseTechList(p.tag, [
    ...(Array.isArray(p.techStack) ? p.techStack : []),
    ...(Array.isArray(p.tech) ? p.tech : []),
  ]);
  const metric = extractMetric(p.highlights);
  const highlights = normalizeHighlights(p.highlights);

  // Related = other visible items in the same category, max 3, excluding self
  const allItems = listData?.collections?.portfolioItems || [];
  const related = allItems
    .filter((it) => it.slug && it.slug !== slug && it.category === p.category)
    .slice(0, 3);
  const fallbackRelated = related.length
    ? related
    : allItems.filter((it) => it.slug && it.slug !== slug).slice(0, 3);

  return (
    <>
      <CinematicHero item={p} reducedMotion={reducedMotion} />

      <OverviewSection item={p} techList={techList} metric={metric} />

      {highlights.length > 0 && (
        <HighlightsSection highlights={highlights} />
      )}

      {gallery.length > 0 && (
        <Gallery images={gallery} title={p.title} reducedMotion={reducedMotion} />
      )}

      {techList.length > 0 && (
        <TechSection tech={techList} />
      )}

      {p.caseStudySlug && <CaseStudyCrosslink slug={p.caseStudySlug} />}

      <PrevNextBand prev={prev} next={next} navigate={navigate} reducedMotion={reducedMotion} />

      {fallbackRelated.length > 0 && <RelatedProjects items={fallbackRelated} />}

      <CtaBand />
    </>
  );
}

/* ─── 1. Cinematic full-bleed hero ─────────────────────────────────────── */

function CinematicHero({ item, reducedMotion }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], reducedMotion ? ['0%', '0%'] : ['0%', '14%']);
  const scale = useTransform(scrollYProgress, [0, 1], reducedMotion ? [1, 1] : [1.05, 1.18]);

  return (
    <section
      ref={ref}
      data-testid="pd-hero"
      className="relative h-[84vh] min-h-[620px] w-full overflow-hidden bg-navy-deep"
    >
      {(item.img || item.image) && (
        <motion.img
          layoutId={reducedMotion ? undefined : `portfolio-img-${item.slug}`}
          src={item.img || item.image}
          alt={item.title}
          loading="eager"
          style={{ y, scale }}
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
        />
      )}

      {/* Light scrim behind the navbar so logo + menu stay readable over dark images */}
      <div
        aria-hidden="true"
        className="absolute top-0 inset-x-0 h-44 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.65) 45%, rgba(255,255,255,0.18) 78%, transparent 100%)',
        }}
      />

      {/* Vignette + reading gradient for the title area at the bottom */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/45 to-transparent" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-navy-deep/55 via-transparent to-navy-deep/25" />

      <div className="relative h-full container mx-auto flex flex-col justify-end pb-48 sm:pb-52 pt-44">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
        >
          <Link
            to="/portfolio"
            data-testid="pd-back-link"
            className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase font-bold text-white/85 hover:text-white transition-colors mb-7"
          >
            <ChevronLeft className="h-4 w-4" /> Back to Portfolio
          </Link>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {item.category && <MetaChip>{prettyCategory(item.category)}</MetaChip>}
            {item.year && <MetaChip>{item.year}</MetaChip>}
            {item.client && <MetaChip>{item.client}</MetaChip>}
          </div>

          <h1
            data-testid="pd-title"
            className="text-5xl sm:text-6xl lg:text-[4.4rem] font-bold tracking-tight text-white leading-[1.0] max-w-4xl drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]"
          >
            {item.title}
          </h1>

          {item.tag && (
            <p className="mt-6 text-[11px] sm:text-xs tracking-[0.3em] uppercase font-bold text-[#E8C25A]">{item.tag}</p>
          )}
        </motion.div>
      </div>

      {/* Subtle bottom scroll-cue */}
      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-[10px] tracking-[0.32em] uppercase font-bold flex flex-col items-center gap-2"
      >
        <span>Scroll</span>
        <motion.span
          animate={reducedMotion ? {} : { y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="h-7 w-[1px] bg-white/40"
        />
      </motion.div>
    </section>
  );
}

function MetaChip({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/12 backdrop-blur ring-1 ring-white/15 text-[11px] font-bold tracking-[0.2em] uppercase text-white">
      {children}
    </span>
  );
}

/* ─── 2. Overview — two-tone brief + spec sheet (overlaps hero) ─────────── */

function OverviewSection({ item, techList, metric }) {
  const stack = techList.slice(0, 4).map((t) => t.name).join(' · ');
  return (
    <section className="relative z-10 -mt-28 sm:-mt-32 pb-2">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.7, ease: EASE }}
          data-testid="pd-glass-card"
          className="relative max-w-6xl mx-auto rounded-[2rem] bg-white/90 backdrop-blur-xl ring-1 ring-white/50 shadow-[0_40px_90px_rgba(15,25,45,0.28)] overflow-hidden"
        >
          <span
            aria-hidden="true"
            className="absolute -top-px left-12 right-12 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#C9962E] to-transparent"
          />
          <div className="grid lg:grid-cols-[1.55fr_1fr]">
            {/* Left — the brief */}
            <div className="p-9 sm:p-12">
              <ChapterHeading eyebrow="Overview" title="The brief" />
              <p className="mt-6 text-lg sm:text-xl text-navy-deep/90 leading-relaxed max-w-xl">
                {item.description ||
                  `${item.title} — a ${prettyCategory(item.category)} project crafted by MGS for ${item.client || 'a partner brand'}.`}
              </p>
              {item.link && <VisitLink href={item.link} className="mt-8" />}
            </div>

            {/* Right — spec sheet on a navy panel */}
            <div className="relative p-9 sm:p-12 bg-navy-deep text-white">
              <span
                aria-hidden="true"
                className="absolute -left-16 -top-16 h-56 w-56 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(232,194,90,0.25), transparent 65%)' }}
              />
              {metric && (
                <div className="relative mb-8 pb-8 border-b border-white/10">
                  <p className="text-[10px] font-bold tracking-[0.24em] uppercase text-[#E8C25A]">Signature outcome</p>
                  <p className="mt-3 text-5xl sm:text-6xl font-bold tracking-tight text-white leading-none">{metric.value}</p>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{metric.text}</p>
                </div>
              )}
              <dl className="relative divide-y divide-white/10">
                <SpecRow label="Client" value={item.client} />
                <SpecRow label="Year" value={item.year} />
                <SpecRow label="Category" value={prettyCategory(item.category)} />
                <SpecRow label="Stack" value={stack} />
              </dl>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SpecRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-6 py-3.5 first:pt-0 last:pb-0">
      <dt className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/45 shrink-0">{label}</dt>
      <dd className="text-right text-[13px] sm:text-sm font-semibold text-white leading-snug">{value}</dd>
    </div>
  );
}

/* ─── 3. Highlights — numbered cards ───────────────────────────────────── */

function HighlightsSection({ highlights }) {
  return (
    <section className="py-24 sm:py-28 bg-sky-50" data-testid="pd-highlights">
      <div className="container mx-auto max-w-6xl">
        <ChapterHeading eyebrow="Highlights" title="Why this project stands out" />

        <ul className="mt-14 grid sm:grid-cols-2 gap-5">
          {highlights.map((h, i) => (
            <motion.li
              key={h + i}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
              className="group relative rounded-2xl bg-white border border-hairline p-7 sm:p-8 overflow-hidden shadow-[0_10px_28px_rgba(31,45,69,0.06)] hover:shadow-[0_24px_54px_rgba(31,45,69,0.14)] hover:-translate-y-1 transition-all duration-300"
            >
              <span
                aria-hidden="true"
                className="absolute right-6 top-5 text-5xl font-bold text-navy-deep/[0.05] group-hover:text-[#C9962E]/15 transition-colors duration-300"
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#2BA8E0]/15 to-[#E8C25A]/20 text-brandblue ring-1 ring-hairline">
                <Check className="h-5 w-5" />
              </span>
              <p className="relative mt-5 text-base sm:text-lg font-medium text-navy-deep leading-relaxed pr-8">{h}</p>
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-[#2BA8E0] via-[#2F6FB0] to-[#E8C25A] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              />
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─── 4. Gallery — arrow carousel + click-to-zoom lightbox ─────────────── */

function Gallery({ images, title, reducedMotion }) {
  const scrollerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(-1);
  const [paused, setPaused] = useState(false);
  const count = images.length;

  // Width of one slide (card + gap), measured from the rendered DOM so it stays
  // correct across breakpoints and container padding.
  const stepSize = () => {
    const el = scrollerRef.current;
    if (!el) return 1;
    const slides = el.querySelectorAll('[data-slide]');
    if (slides.length >= 2) {
      return slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left;
    }
    return el.clientWidth || 1;
  };

  const goTo = (i, smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    const ni = ((i % count) + count) % count;
    el.scrollTo({ left: stepSize() * ni, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
    setIndex(ni);
  };

  // Keep the active index in sync with manual / native-swipe scrolling.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(el.scrollLeft / stepSize());
        setIndex(Math.max(0, Math.min(count - 1, i)));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [count]);

  // Gentle auto-advance — pauses on hover/focus and while the lightbox is open.
  useEffect(() => {
    if (reducedMotion || paused || lightbox !== -1 || count <= 1) return undefined;
    const id = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const step = stepSize();
      const ni = (Math.round(el.scrollLeft / step) + 1) % count;
      el.scrollTo({ left: step * ni, behavior: 'smooth' });
      setIndex(ni);
    }, 4200);
    return () => clearInterval(id);
  }, [reducedMotion, paused, lightbox, count]);

  return (
    <section className="py-24 sm:py-28 bg-white relative overflow-hidden" data-testid="pd-gallery">
      <div className="container mx-auto flex flex-wrap items-end justify-between gap-4 mb-12">
        <ChapterHeading eyebrow="Gallery" title={`Inside ${title}`} />
        {count > 1 && (
          <div className="hidden sm:flex items-center gap-3" data-testid="pd-gallery-arrows">
            <GalleryArrow dir="left" onClick={() => goTo(index - 1)} label="Previous screen" />
            <GalleryArrow dir="right" onClick={() => goTo(index + 1)} label="Next screen" />
          </div>
        )}
      </div>

      <div
        ref={scrollerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="overflow-x-auto no-scrollbar pb-2"
      >
        <div className="container mx-auto flex gap-6 sm:gap-8">
          {images.map((src, i) => (
            <motion.figure
              key={src + i}
              data-slide=""
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: EASE }}
              className="shrink-0 w-[82vw] sm:w-[560px] lg:w-[680px]"
            >
              <button
                type="button"
                onClick={() => setLightbox(i)}
                aria-label={`View ${title} screen ${i + 1} larger`}
                className="group relative block w-full rounded-2xl overflow-hidden ring-1 ring-hairline shadow-[0_20px_50px_rgba(31,45,69,0.12)] cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E] focus-visible:ring-offset-2"
              >
                <span className="absolute left-4 top-4 z-10 inline-flex items-center justify-center h-8 px-3 rounded-full bg-white/85 backdrop-blur text-[11px] font-bold tracking-[0.18em] text-navy-deep">
                  {String(i + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                </span>
                <span className="absolute right-4 top-4 z-10 h-9 w-9 rounded-full bg-white/90 backdrop-blur inline-flex items-center justify-center text-navy-deep opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                  <Maximize2 className="h-4 w-4" />
                </span>
                <div className="relative aspect-[16/10] overflow-hidden bg-sky-100">
                  <img
                    src={src}
                    alt={`${title} screenshot ${i + 1}`}
                    loading="lazy"
                    draggable={false}
                    className="w-full h-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-[1.04] will-change-transform"
                  />
                </div>
              </button>
            </motion.figure>
          ))}
          <div className="shrink-0 w-6" aria-hidden="true" />
        </div>
      </div>

      {count > 1 && (
        <div className="container mx-auto mt-9 flex items-center justify-center gap-2.5" data-testid="pd-gallery-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to screen ${i + 1}`}
              aria-current={i === index}
              className={cn(
                'h-2 rounded-full transition-all duration-300',
                i === index ? 'w-7 bg-gradient-to-r from-[#C9962E] to-[#E8C25A]' : 'w-2 bg-hairline2 hover:bg-slatebody/40'
              )}
            />
          ))}
        </div>
      )}

      <Lightbox images={images} index={lightbox} title={title} onClose={() => setLightbox(-1)} onIndex={setLightbox} />
    </section>
  );
}

function GalleryArrow({ dir, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="h-12 w-12 rounded-full bg-white border border-hairline inline-flex items-center justify-center text-navy-deep shadow-[0_6px_18px_rgba(31,45,69,0.08)] hover:bg-navy-deep hover:text-white hover:border-navy-deep transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9962E] focus-visible:ring-offset-2"
    >
      {dir === 'left' ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
    </button>
  );
}

function Lightbox({ images, index, title, onClose, onIndex }) {
  const open = index >= 0;
  const count = images.length;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((index + 1) % count);
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, index, count, onClose, onIndex]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="pd-gallery-lightbox"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-deep/90 backdrop-blur-sm p-4 sm:p-10"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {count > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + count) % count); }}
              aria-label="Previous screen"
              className="absolute left-3 sm:left-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.28, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-6xl"
          >
            <img
              src={images[index]}
              alt={`${title} screen ${index + 1}`}
              className="w-full max-h-[82vh] object-contain rounded-xl shadow-[0_40px_90px_rgba(0,0,0,0.6)]"
            />
            <figcaption className="mt-4 text-center text-white/70 text-[12px] tracking-[0.18em] uppercase font-bold">
              {title} — {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
            </figcaption>
          </motion.figure>

          {count > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % count); }}
              aria-label="Next screen"
              className="absolute right-3 sm:right-8 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── 6. Tech-stack chips grid ─────────────────────────────────────────── */

function TechSection({ tech }) {
  return (
    <section className="py-24 sm:py-28 bg-sky-50 border-y border-hairline overflow-hidden" data-testid="pd-tech-marquee">
      <div className="container mx-auto">
        <ChapterHeading eyebrow="Tech Stack" title="Built with battle-tested tools" align="center" />

        <div className="mt-12 flex flex-wrap justify-center gap-3 sm:gap-4 max-w-4xl mx-auto">
          {tech.map((t, i) => (
            <motion.span
              key={(t.name || 'tech') + i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.45, delay: i * 0.05, ease: EASE }}
              whileHover={{ y: -3 }}
              className="group inline-flex items-center gap-2.5 rounded-full bg-white border border-hairline px-5 sm:px-6 py-3 shadow-[0_6px_18px_rgba(31,45,69,0.05)] hover:border-brandblue/40 hover:shadow-[0_14px_30px_rgba(31,45,69,0.13)] transition-all"
              title={t.name}
            >
              {t.icon_url ? (
                <img src={t.icon_url} alt={t.name} loading="lazy" className="h-6 w-6 object-contain" />
              ) : (
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-[#2BA8E0]/15 to-[#E8C25A]/25">
                  <Sparkles className="h-3.5 w-3.5 text-brandblue" />
                </span>
              )}
              <span className="text-sm font-semibold tracking-wide text-navy-deep">{t.name}</span>
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 7. Case-study crosslink ──────────────────────────────────────────── */

function CaseStudyCrosslink({ slug }) {
  return (
    <section className="py-20 sm:py-24 bg-white" data-testid="pd-cs-crosslink">
      <div className="container mx-auto">
        <Link
          to={`/case-studies/${slug}`}
          className="group block rounded-3xl p-10 sm:p-12 bg-gradient-to-br from-navy-deep via-[#1F4373] to-[#2F6FB0] text-white overflow-hidden relative shadow-[0_30px_60px_rgba(15,25,45,0.25)]"
        >
          <span aria-hidden="true" className="absolute -right-16 -top-16 h-72 w-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(232,194,90,0.35), transparent 65%)' }} />
          <div className="relative grid lg:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#E8C25A]">The story behind it</p>
              <h3 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Read the full case study →
              </h3>
              <p className="mt-3 text-white/85 text-base sm:text-lg max-w-xl leading-relaxed">
                Get the long-form story: the challenges we faced, how we built it, and the numbers that came out.
              </p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-navy-deep text-[11px] tracking-[0.22em] uppercase font-bold group-hover:bg-[#E8C25A] transition-colors">
              View case study
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      </div>
    </section>
  );
}

/* ─── 8. Magnetic Prev / Next band ─────────────────────────────────────── */

function PrevNextBand({ prev, next, navigate, reducedMotion }) {
  if (!prev && !next) return null;
  return (
    <section className="py-16 bg-sky-50 border-t border-hairline" data-testid="pd-prev-next">
      <div className="container mx-auto flex items-center justify-between gap-6">
        <MagneticNavCard
          testId="pd-prev"
          label="Previous project"
          slug={prev}
          align="left"
          onClick={() => prev && navigate(`/portfolio/${prev}`)}
          reducedMotion={reducedMotion}
        />
        <MagneticNavCard
          testId="pd-next"
          label="Next project"
          slug={next}
          align="right"
          onClick={() => next && navigate(`/portfolio/${next}`)}
          reducedMotion={reducedMotion}
        />
      </div>
    </section>
  );
}

function MagneticNavCard({ testId, label, slug, align, onClick, reducedMotion }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 200, damping: 18, mass: 0.5 });
  const y = useSpring(my, { stiffness: 200, damping: 18, mass: 0.5 });

  const onMove = (e) => {
    if (!slug || reducedMotion) return;
    const r = ref.current?.getBoundingClientRect?.();
    if (!r) return;
    const px = e.clientX - (r.left + r.width / 2);
    const py = e.clientY - (r.top + r.height / 2);
    mx.set(Math.max(-8, Math.min(8, px * 0.16)));
    my.set(Math.max(-6, Math.min(6, py * 0.16)));
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const disabled = !slug;

  return (
    <motion.button
      ref={ref}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileTap={disabled ? {} : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 360, damping: 22 }}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={label}
      className={`group inline-flex items-center gap-3 text-left disabled:opacity-30 disabled:cursor-not-allowed ${align === 'right' ? 'flex-row-reverse text-right' : ''}`}
    >
      <span className="h-14 w-14 rounded-full bg-white border border-hairline inline-flex items-center justify-center text-navy-deep group-hover:bg-navy-deep group-hover:text-white transition-colors">
        {align === 'right' ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
      </span>
      <span>
        <span className="block text-[10px] font-bold tracking-[0.22em] uppercase text-slatebody/70">{label}</span>
        <span className="block mt-1 text-[15px] font-semibold tracking-tight text-navy-deep group-hover:text-[#C9962E] transition-colors capitalize">
          {slug ? slug.replace(/-/g, ' ') : '—'}
        </span>
      </span>
    </motion.button>
  );
}

/* ─── 9. Related projects ──────────────────────────────────────────────── */

function RelatedProjects({ items }) {
  return (
    <section className="py-20 sm:py-24 bg-white" data-testid="pd-related">
      <div className="container mx-auto">
        <ChapterHeading eyebrow="More work" title="Related projects" />

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r, i) => (
            <motion.div
              key={r.slug || r.title}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportOnce}
              transition={{ duration: 0.55, delay: i * 0.07, ease: EASE }}
            >
              <Link
                to={`/portfolio/${r.slug}`}
                className="group block rounded-2xl overflow-hidden bg-white border border-hairline shadow-[0_10px_28px_rgba(31,45,69,0.08)] hover:shadow-[0_22px_50px_rgba(31,45,69,0.16)] hover:-translate-y-1.5 transition-all duration-300"
                data-testid={`pd-related-${i}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-sky-100">
                  {(r.image || r.img) ? (
                    <img src={r.image || r.img} alt={r.title} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms] group-hover:scale-[1.06] will-change-transform" />
                  ) : (
                    <div aria-hidden="true" className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #E6F1FB 0%, #C9DCEF 55%, #E8C25A20 100%)' }} />
                  )}
                  <span className="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/90 backdrop-blur inline-flex items-center justify-center text-navy-deep opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
                <div className="p-6">
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-brandblue">{prettyCategory(r.category) || 'Project'}</p>
                  <h4 className="mt-2.5 text-[15px] font-semibold tracking-tight text-navy-deep leading-snug line-clamp-2 group-hover:text-[#C9962E] transition-colors">{r.title}</h4>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

